import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMsg } from "../lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, CheckCircle, KeyRound } from "lucide-react";
import {
  FloatingLabelInput, GradientButton, AuthSplitLeft, AuthHeader, AuthFooterNote,
} from "../components/AuthUI";
import usePageTitle from "../lib/usePageTitle";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  usePageTitle("Reset Password | HouseEntry");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setSent(r.data);
      if (r.data.demo && r.data.reset_link) {
        toast.success("Demo: reset link generated below");
      } else {
        toast.success("If that email exists, a reset link has been sent.");
      }
    } catch (err) {
      toast.error(apiErrorMsg(err, "Could not send reset email"));
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-screen bg-zinc-50" data-testid="forgot-page">
      <AuthSplitLeft
        eyebrow="HouseEntry · Account Recovery"
        heading="Locked out? We'll get you back in."
        sub="Enter the email associated with your HouseEntry account and we'll send you a secure reset link."
      />

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <div className="auth-blobs lg:hidden opacity-30" />
        <div className="w-full max-w-md relative">
          <AuthHeader showBack backTo="/login" />

          <div className="auth-glass rounded-2xl p-7 sm:p-8" data-testid="forgot-form">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#0055FF] mb-3 inline-flex items-center gap-1.5">
              <KeyRound size={11} /> Reset Password
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-zinc-900">Forgot password</h1>
            <p className="text-[14px] text-zinc-600 mt-1.5 mb-6">
              Enter your email and we'll send a reset link, valid for 1 hour.
            </p>

            {!sent && (
              <form onSubmit={submit} className="space-y-4">
                <FloatingLabelInput
                  id="forgot-email" type="email" label="Email address" icon={Mail}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoFocus autoComplete="email" testid="forgot-email"
                />
                <GradientButton type="submit" loading={busy} testid="forgot-submit" icon={ArrowRight}>
                  Send Reset Link
                </GradientButton>
              </form>
            )}

            {sent && (
              <div className="text-center py-5 space-y-4" data-testid="forgot-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="font-heading font-bold text-xl text-zinc-900">Check your inbox</h3>
                <p className="text-[13px] text-zinc-600">
                  Agar account exist karta hai, toh aapko ek reset link mil gaya hoga.
                </p>
                {sent.demo && sent.reset_link && (
                  <div className="p-3.5 border border-amber-200 bg-amber-50 rounded-xl text-left" data-testid="forgot-demo-link">
                    <div className="font-bold text-amber-800 text-[12px] mb-1.5">DEMO mode — open directly</div>
                    <a
                      href={sent.reset_link.replace(window.location.origin, "")}
                      className="text-[#0055FF] break-all hover:underline text-[12px] font-mono"
                    >
                      {sent.reset_link}
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-zinc-200 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#0055FF] hover:underline" data-testid="back-to-login">
                <ArrowLeft size={13} /> Back to sign in
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <AuthFooterNote />
          </div>
        </div>
      </div>
    </div>
  );
}
